import { questionSequence } from "../Helpers.tsx";
import { ProblemData } from "../../MathFactsGenerator/Constants.ts";
import { ISettingsProps } from "../../MathFactsGenerator/Models.ts";

/**
 * The generation loop eighteen `generateXPages(settings)` functions wrote out
 * by hand, one copy at a time, until it had produced two defect classes across
 * twenty-seven instances. See ADR 0042.
 *
 * A generator keeps only what is actually its own: how to build one problem
 * (`build`), what makes two problems the same (`fingerprint`), and the list of
 * variants a sheet rotates fairly over (`choices`) - question types, operators,
 * coin kinds, measurement systems, whatever the format calls it. A generator
 * with nothing to vary passes a single-element list; there is no overload that
 * takes a raw index; see ADR 0042 §2 for why not.
 *
 * `build` returning `null` counts as a spent attempt and is skipped - the
 * pattern every generator already used for a draw that turned out unusable
 * (an accidental tie, a subtraction of equal values). `fingerprint` is a pure
 * function of the finished problem, never of the choice that produced it: every
 * generator converted onto this helper already recorded enough of its own
 * shape on `ProblemData` (the format's own optional field) for `fingerprint`
 * to recover whatever it needs.
 */
export interface ProblemPagesOptions<T> {
    settings: ISettingsProps;
    /** The fair, shuffled rotation is built from this list (ADR 0041 §1). */
    choices: T[];
    /** Builds one problem from a choice and its 1-based position on the sheet.
     *  `null` means the draw is not worth printing; it is skipped and retried. */
    build: (choice: T, key: number) => ProblemData | null;
    /** What counts as the same problem twice, for the de-duplication guard. */
    fingerprint: (problem: ProblemData) => string;
    /**
     * Deal `choices` evenly within each page (and so within each half of a
     * half-page sheet, which is generated as two sheets) rather than across the
     * whole run. Without it a multi-page sheet is even over all its pages and a
     * page may be lopsided; a generator whose pages are each handed out on their
     * own opts in.
     */
    dealPerPage?: boolean;
    /**
     * One page's sequence of choices, for a generator whose choices are a
     * crossing of two things (operation by kind) and whose pages must be even
     * in both, which a fair shuffle of the crossed list is not: it can hand
     * the extras to two choices that share an operation. Replaces
     * `questionSequence`, per page, and implies `dealPerPage`.
     */
    dealPage?: (perPage: number) => T[];
}

/**
 * A page-shaped set of problems, or `null` when nothing could be built at all.
 *
 * Owns the attempt budget, the fair sequence over `choices`, the accept/reject
 * loop and its give-way rule, and the slicing into pages - everything the
 * eighteen copies shared. `total * 40` and the half-budget give-way are not
 * reconsidered here; ADR 0042 §Out of scope leaves that a separate decision.
 */
export function generateProblemPages<T>(options: ProblemPagesOptions<T>): ProblemData[][] | null {
    const { settings, choices, build, fingerprint, dealPerPage, dealPage } = options;
    const perPage = settings.NumberOfProblemsPerPage;
    const total = settings.NumberOfPages * perPage;
    // ADR 0041 §1 / ADR 0042: a fair, shuffled sequence, not
    // `choices[problems.length % choices.length]` - that rotation always gave
    // the extra problem to the same leading choices.
    const sequence = dealPage
        ? Array.from({ length: settings.NumberOfPages }, () => dealPage(perPage)).flat()
        : dealPerPage
        ? Array.from({ length: settings.NumberOfPages }, () => questionSequence(choices, perPage)).flat()
        : questionSequence(choices, total);

    const problems: ProblemData[] = [];
    const seen = new Set<string>();
    let attempts = 0;
    const attemptLimit = total * 40;

    while (problems.length < total && attempts < attemptLimit) {
        attempts++;
        const choice = sequence[problems.length];
        const problem = build(choice, problems.length + 1);
        if (problem === null) {
            continue;
        }
        const fp = fingerprint(problem);
        // Distinctness for the first half of the budget, then filling the page
        // wins. The budget is what gives way, not a count of problems seen,
        // which only grows when one is accepted (ADR 0036).
        if (seen.has(fp) && attempts < attemptLimit / 2) {
            continue;
        }
        seen.add(fp);
        problems.push(problem);
    }

    if (problems.length === 0) {
        return null;
    }

    const pages: ProblemData[][] = [];
    for (let page = 0; page < settings.NumberOfPages; page++) {
        pages.push(problems.slice(page * perPage, (page + 1) * perPage));
    }
    return pages;
}
